/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Jan 15 2024
 **/

import { ComponentProps, CSSProperties, useCallback, useMemo } from "react";

import { Avatar as AvatarDesign } from "@/frameworks/designStuff/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { useMouseEnterWait } from "@/frameworks/libraries/hooks";
import { Maybe } from "@/frameworks/libraries/components";
import { usePopover } from "@/frameworks/core/hooks";
import { GROUP_POPOVER_ID } from "@/frameworks/core/commons";
import { usePersistUser } from "@/frameworks/core/persistence";
import { MiniProfile } from "../MiniProfile";
import { AvatarStatusColorMap } from "./AvatarMapData";

type CoreAvatarProps = Pick<ComponentProps<typeof AvatarDesign>, "kind"> & {
	id: string;
	useStatus?: boolean;
	showMiniProfile?: boolean;
	mySelf?: boolean;
	style?: CSSProperties;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOGTAG = "#Avatar#"; // 삭제하지 말것

export default function Avatar({ showMiniProfile = true, ...props }: CoreAvatarProps) {
	const { id } = props;
	return (
		<div css={{ position: "relative" }}>
			<AvatarComp {...props} />
			{showMiniProfile && <AvatarMiniProfile userID={id} />}
		</div>
	);
}

const AvatarComp = ({ id, useStatus, kind, ...props }: CoreAvatarProps) => {
	const user = usePersistUser(id === "me" ? Configure.userID : id);
	const activeStatus = useMemo(() => user?.activeStatus ?? "OFFLINE", [user]);

	return (
		<Maybe test={Boolean(user)}>
			<AvatarDesign
				key={id}
				status={useStatus ? AvatarStatusColorMap[activeStatus] ?? "Gray" : undefined}
				src={user?.profileImagePath}
				text={user?.name ?? ""}
				kind={kind}
				textColor={`${user?.colorProfile}_50_100` as Avatar.ProfileColor}
				type="image"
				style={props.style}
			/>
			<Maybe.Fallback>
				<AvatarDesign key={`notfound-${id}`} type="image" kind={kind} text={id} />
			</Maybe.Fallback>
		</Maybe>
	);
};

const AvatarMiniProfile = ({ userID }: { userID: string }) => {
	const popover = usePopover();

	const onMouseLeave = (e: React.MouseEvent) => {
		const target = e.relatedTarget;
		if (target instanceof HTMLElement && target.closest('[data-role="call-method-option"]')) return;
		popover.hide(GROUP_POPOVER_ID.AVATAR_MINI_PROFILE);
	};

	const handleMouseEnter = useCallback(
		(ev: MouseEvent) => {
			if (!userID) return;
			const target = ev.target as HTMLDivElement;
			const { top, left } = target.getBoundingClientRect();
			popover.show({
				popoverID: GROUP_POPOVER_ID.AVATAR_MINI_PROFILE,
				uniqueID: userID,
				content: (
					<div onMouseLeave={onMouseLeave}>
						<>
							<MiniProfile userID={userID} />
						</>
					</div>
				),
				anchorEl: document.body,
				position: {
					x: left,
					y: top
				}
			});
		},
		[userID]
	);

	const { ref } = useMouseEnterWait<HTMLDivElement>(handleMouseEnter);

	return <div css={{ position: "absolute", width: "100%", height: "100%", top: 0 }} ref={ref} />;
};
